import { Question } from '../types/assessment';

export const traumaStressQuestions: Question[] = [
  // Current Stress Level
  {
    id: 'current_stress_level',
    text: 'How would you rate your current overall stress level?',
    type: 'scale',
    category: 'stress',
    subcategory: 'current',
    required: true,
    options: [
      { value: 1, label: '1 - Very low stress', score: 1 },
      { value: 2, label: '2 - Low stress', score: 2 },
      { value: 3, label: '3 - Moderate stress', score: 3 },
      { value: 4, label: '4 - High stress', score: 4 },
      { value: 5, label: '5 - Extremely high stress', score: 5 }
    ]
  },
  {
    id: 'stress_sources',
    text: 'What are your main sources of stress? (Select all that apply)',
    type: 'multi-select',
    category: 'stress',
    subcategory: 'sources',
    required: false,
    options: [
      { value: 'work-job', label: 'Work or job responsibilities' },
      { value: 'financial', label: 'Financial concerns' },
      { value: 'family-relationships', label: 'Family relationships' },
      { value: 'romantic-relationship', label: 'Romantic relationship' },
      { value: 'health-concerns', label: 'Health concerns (self or loved ones)' },
      { value: 'parenting', label: 'Parenting responsibilities' },
      { value: 'caregiving', label: 'Caregiving for elderly or disabled family' },
      { value: 'academic', label: 'Academic or educational pressures' },
      { value: 'social-situations', label: 'Social situations' },
      { value: 'major-life-changes', label: 'Major life changes or transitions' },
      { value: 'legal-issues', label: 'Legal issues' },
      { value: 'housing', label: 'Housing or living situation' },
      { value: 'discrimination', label: 'Discrimination or prejudice' },
      { value: 'world-events', label: 'World events or news' },
      { value: 'other', label: 'Other sources' }
    ]
  },
  {
    id: 'stress_management',
    text: 'How well do you feel you manage stress?',
    type: 'multiple-choice',
    category: 'stress',
    subcategory: 'management',
    required: true,
    options: [
      { value: 'very-well', label: 'Very well', score: 4 },
      { value: 'well', label: 'Well', score: 3 },
      { value: 'somewhat', label: 'Somewhat', score: 2 },
      { value: 'poorly', label: 'Poorly', score: 1 },
      { value: 'very-poorly', label: 'Very poorly', score: 0 }
    ]
  },
  
  // Coping Mechanisms
  {
    id: 'coping_strategies',
    text: 'What strategies do you use to cope with stress? (Select all that apply)',
    type: 'multi-select',
    category: 'stress',
    subcategory: 'coping',
    required: false,
    options: [
      { value: 'exercise', label: 'Exercise or physical activity' },
      { value: 'meditation', label: 'Meditation or mindfulness' },
      { value: 'deep-breathing', label: 'Deep breathing exercises' },
      { value: 'talking-to-others', label: 'Talking to friends or family' },
      { value: 'professional-help', label: 'Professional counseling or therapy' },
      { value: 'hobbies', label: 'Hobbies or creative activities' },
      { value: 'music', label: 'Listening to music' },
      { value: 'reading', label: 'Reading' },
      { value: 'nature', label: 'Spending time in nature' },
      { value: 'prayer-spirituality', label: 'Prayer or spiritual practices' },
      { value: 'journaling', label: 'Journaling or writing' },
      { value: 'sleep', label: 'Getting extra sleep' },
      { value: 'alcohol', label: 'Alcohol use' },
      { value: 'substances', label: 'Other substances' },
      { value: 'avoidance', label: 'Avoiding stressful situations' },
      { value: 'shopping', label: 'Shopping or spending money' },
      { value: 'overeating', label: 'Eating more than usual' },
      { value: 'social-media', label: 'Social media or internet browsing' },
      { value: 'none', label: 'I don\'t have effective coping strategies' }
    ]
  },
  
  // Trauma History (Sensitive)
  {
    id: 'trauma_intro',
    text: 'The following questions ask about difficult life experiences. These experiences are unfortunately common. Please answer honestly, but know that you can skip any question that feels too uncomfortable.',
    type: 'text',
    category: 'trauma',
    subcategory: 'intro',
    required: false,
    validationRules: []
  },
  {
    id: 'adverse_childhood',
    text: 'Before age 18, did you experience any of the following? (Select all that apply, or select "Prefer not to answer")',
    type: 'multi-select',
    category: 'trauma',
    subcategory: 'childhood',
    required: false,
    options: [
      { value: 'physical-abuse', label: 'Physical abuse' },
      { value: 'emotional-abuse', label: 'Emotional abuse or neglect' },
      { value: 'sexual-abuse', label: 'Sexual abuse' },
      { value: 'witness-violence', label: 'Witnessed domestic violence' },
      { value: 'substance-abuse-household', label: 'Substance abuse in household' },
      { value: 'mental-illness-household', label: 'Mental illness in household' },
      { value: 'parent-separation', label: 'Parent separation or divorce' },
      { value: 'incarcerated-family', label: 'Family member incarcerated' },
      { value: 'bullying', label: 'Severe bullying or peer victimization' },
      { value: 'medical-trauma', label: 'Serious medical trauma or illness' },
      { value: 'natural-disaster', label: 'Natural disaster or accident' },
      { value: 'none', label: 'None of these' },
      { value: 'prefer-not-answer', label: 'Prefer not to answer' }
    ]
  },
  {
    id: 'adult_trauma',
    text: 'As an adult, have you experienced any of the following? (Select all that apply, or select "Prefer not to answer")',
    type: 'multi-select',
    category: 'trauma',
    subcategory: 'adult',
    required: false,
    options: [
      { value: 'physical-assault', label: 'Physical assault or violence' },
      { value: 'sexual-assault', label: 'Sexual assault or rape' },
      { value: 'domestic-violence', label: 'Domestic violence' },
      { value: 'serious-accident', label: 'Serious accident or injury' },
      { value: 'military-combat', label: 'Military combat exposure' },
      { value: 'natural-disaster', label: 'Natural disaster' },
      { value: 'sudden-death', label: 'Sudden death of loved one' },
      { value: 'life-threatening-illness', label: 'Life-threatening illness (self or loved one)' },
      { value: 'workplace-trauma', label: 'Workplace trauma or violence' },
      { value: 'robbery-crime', label: 'Robbery or other violent crime' },
      { value: 'medical-trauma', label: 'Medical trauma or malpractice' },
      { value: 'discrimination-violence', label: 'Violence due to discrimination' },
      { value: 'none', label: 'None of these' },
      { value: 'prefer-not-answer', label: 'Prefer not to answer' }
    ]
  },
  {
    id: 'trauma_impact',
    text: 'If you have experienced any difficult or traumatic life experiences, how much do they affect your daily life now?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'impact',
    required: true,
    options: [
      { value: 'no-impact', label: 'No impact - I\'ve recovered well', score: 0 },
      { value: 'minimal-impact', label: 'Minimal impact', score: 1 },
      { value: 'moderate-impact', label: 'Moderate impact on some areas', score: 2 },
      { value: 'significant-impact', label: 'Significant impact on daily functioning', score: 3, triggerConditions: ['trauma_concern'] },
      { value: 'severe-impact', label: 'Severe impact - affects most areas of life', score: 4, triggerConditions: ['trauma_concern', 'ptsd_screening'] }
    ]
  },
  
  // PTSD Screening (PC-PTSD-5 based)
  {
    id: 'ptsd_intro',
    text: 'Sometimes things happen to people that are unusually or especially frightening, horrible, or traumatic. If you\'ve experienced such events, please answer the following questions about your experiences in the past month:',
    type: 'text',
    category: 'trauma',
    subcategory: 'ptsd',
    required: false,
    validationRules: []
  },
  {
    id: 'ptsd_nightmares',
    text: 'In the past month, have you had nightmares about traumatic event(s) or thought about them when you did not want to?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'ptsd',
    required: true,
    options: [
      { value: true, label: 'Yes', score: 1 },
      { value: false, label: 'No', score: 0 },
      { value: 'not-applicable', label: 'Not applicable - I have not experienced trauma', score: 0 }
    ]
  },
  {
    id: 'ptsd_avoidance',
    text: 'In the past month, have you tried hard not to think about traumatic event(s) or went out of your way to avoid situations that reminded you of them?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'ptsd',
    required: true,
    options: [
      { value: true, label: 'Yes', score: 1 },
      { value: false, label: 'No', score: 0 },
      { value: 'not-applicable', label: 'Not applicable - I have not experienced trauma', score: 0 }
    ]
  },
  {
    id: 'ptsd_hypervigilance',
    text: 'In the past month, have you been constantly on guard, watchful, or easily startled?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'ptsd',
    required: true,
    options: [
      { value: true, label: 'Yes', score: 1 },
      { value: false, label: 'No', score: 0 },
      { value: 'not-applicable', label: 'Not applicable - I have not experienced trauma', score: 0 }
    ]
  },
  {
    id: 'ptsd_numbness',
    text: 'In the past month, have you felt numb or detached from people, activities, or your surroundings?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'ptsd',
    required: true,
    options: [
      { value: true, label: 'Yes', score: 1 },
      { value: false, label: 'No', score: 0 },
      { value: 'not-applicable', label: 'Not applicable - I have not experienced trauma', score: 0 }
    ]
  },
  {
    id: 'ptsd_guilt',
    text: 'In the past month, have you felt guilty or unable to stop blaming yourself or others for traumatic event(s) or any problems they may have caused?',
    type: 'multiple-choice',
    category: 'trauma',
    subcategory: 'ptsd',
    required: true,
    options: [
      { value: true, label: 'Yes', score: 1 },
      { value: false, label: 'No', score: 0 },
      { value: 'not-applicable', label: 'Not applicable - I have not experienced trauma', score: 0 }
    ]
  }
]; 